import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight, Printer, MapPin, Wallet, CreditCard } from "lucide-react";

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0 Sun .. 6 Sat
  const diff = (day + 6) % 7; // days since Monday
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}
function isoDate(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function prettyDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}
function prettyRange(a, b) {
  return new Date(a + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })
    + " – " + new Date(b + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function RunSheet() {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);

  const start = isoDate(weekStart);
  const end = isoDate(addDays(weekStart, 6));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/admin/run-sheet", { params: { start, end } })
      .then((r) => { if (!cancelled) setStops(r.data.stops); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [start, end]);

  // Group: date -> street_key -> stops[]
  const grouped = useMemo(() => {
    const byDate = {};
    for (const s of stops) {
      if (!byDate[s.date]) byDate[s.date] = {};
      const k = s.street_key || "(unknown street)";
      if (!byDate[s.date][k]) byDate[s.date][k] = [];
      byDate[s.date][k].push(s);
    }
    return byDate;
  }, [stops]);

  const totalRevenue = stops.reduce((s, x) => s + (x.total_price || 0), 0);
  const cashOwed = stops.filter((s) => s.payment_method === "cash" && s.payment_status === "unpaid").reduce((s, x) => s + (x.total_price || 0), 0);

  return (
    <div data-testid="admin-run-sheet">
      <div className="mt-8 flex items-center justify-between gap-4 flex-wrap print:hidden">
        <div className="flex items-center gap-3">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="run-prev-week" className="p-2 border border-deep-forest/15 hover:bg-deep-forest hover:text-warm-sand transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="font-serif text-2xl text-deep-forest" data-testid="run-week-range">{prettyRange(start, end)}</div>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="run-next-week" className="p-2 border border-deep-forest/15 hover:bg-deep-forest hover:text-warm-sand transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="text-xs uppercase tracking-widest text-deep-forest/60 hover:text-terracotta ml-2">This week</button>
        </div>
        <button onClick={() => window.print()} data-testid="run-print-btn" className="btn-secondary text-sm">
          <Printer className="w-4 h-4" /> Print
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-px bg-deep-forest/10 border border-deep-forest/10">
        <div className="bg-bone-white p-5"><div className="label-overline">Stops this week</div><div className="font-serif text-3xl text-deep-forest mt-2" data-testid="run-stat-stops">{stops.length}</div></div>
        <div className="bg-bone-white p-5"><div className="label-overline">Revenue</div><div className="font-serif text-3xl text-deep-forest mt-2" data-testid="run-stat-revenue">${totalRevenue.toFixed(2)}</div></div>
        <div className="bg-bone-white p-5"><div className="label-overline">Cash to collect</div><div className="font-serif text-3xl text-terracotta mt-2" data-testid="run-stat-cash">${cashOwed.toFixed(2)}</div></div>
      </div>

      {loading ? (
        <div className="mt-8 text-sm text-deep-forest/60">Loading…</div>
      ) : stops.length === 0 ? (
        <div className="mt-8 bg-bone-white border border-deep-forest/10 p-10 text-center text-deep-forest/60">No mows scheduled for this week.</div>
      ) : (
        <div className="mt-8 space-y-10">
          {Object.keys(grouped).sort().map((date) => (
            <div key={date} data-testid={`run-day-${date}`} className="border-t border-deep-forest/15 pt-6">
              <div className="flex items-baseline justify-between mb-4">
                <div className="font-serif text-3xl text-deep-forest tracking-tight">{prettyDate(date)}</div>
                <div className="text-sm text-deep-forest/60">{Object.values(grouped[date]).reduce((s, arr) => s + arr.length, 0)} stop{Object.values(grouped[date]).reduce((s, arr) => s + arr.length, 0) > 1 ? "s" : ""}</div>
              </div>
              <div className="space-y-4">
                {Object.keys(grouped[date]).sort().map((street) => {
                  const list = grouped[date][street];
                  return (
                    <div key={street} className="bg-bone-white border border-deep-forest/10" data-testid={`run-street-${date}-${street}`}>
                      <div className="px-4 py-2 bg-moss/50 border-b border-deep-forest/10 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-terracotta" />
                        <div className="font-medium capitalize text-deep-forest">{street}</div>
                        <div className="text-xs uppercase tracking-widest text-deep-forest/60 ml-auto">{list.length} stop{list.length > 1 ? "s" : ""}</div>
                      </div>
                      <table className="w-full text-sm">
                        <tbody>
                          {list.map((s) => (
                            <tr key={s.booking_id} className="border-b border-deep-forest/5 last:border-b-0" data-testid={`run-stop-${s.booking_id}-${s.date}`}>
                              <td className="py-3 pl-4 pr-2 w-48">
                                <div className="font-medium text-deep-forest">{s.customer_name}</div>
                                <div className="text-xs text-deep-forest/50">{s.customer_email}</div>
                              </td>
                              <td className="py-3 px-2 text-deep-forest/80">{s.address}</td>
                              <td className="py-3 px-2 text-deep-forest/80 whitespace-nowrap">{s.areas_count} area{s.areas_count > 1 ? "s" : ""} · {s.total_area} m²</td>
                              <td className="py-3 px-2 whitespace-nowrap">
                                <div className="flex items-center gap-1.5 text-sm">
                                  {s.payment_method === "cash" ? <Wallet className="w-3.5 h-3.5 text-terracotta" /> : <CreditCard className="w-3.5 h-3.5 text-deep-forest/60" />}
                                  <span className={s.payment_method === "cash" && s.payment_status === "unpaid" ? "text-terracotta font-medium" : "text-deep-forest/80"}>
                                    ${s.total_price.toFixed(2)}{s.payment_method === "cash" && s.payment_status === "unpaid" ? " cash" : ""}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 pr-4 pl-2 text-xs text-deep-forest/60 italic">{s.notes || ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
